<template>
  <div class="product-families-show-view">
    <loader v-if="isLoading" />
    <div v-if="isSuccessfullyLoaded">
      <div class="action-buttons-container">
        <div class="action-buttons">
          <link-button :to="{ name: 'edit-product-family', params: { id: productFamily.id } }" icon="pencil">{{
      $t('actions.edit') }}</link-button>

          <link-button :to="{ name: 'product-family-revisions', params: { id: productFamily.id } }" icon="future">{{
      $t('actions.editRevisions') }}</link-button>

          <common-button @click="deleteProductFamilyWhenConfirmed" con="trash" type="danger">{{ $t('actions.delete')
            }}</common-button>
        </div>

        <div class="active-revision-indicator" v-if="hasActiveRevision">
          <icon name="check" />
          <b> {{ $t('hints.activeRevision') }} </b>
        </div>
      </div>

      <h3 v-if="productFamily.bundle">{{ $t('headlines.bundledFamilies') }}</h3>

      <product-family-bundling-manager :product-family="productFamily" v-if="productFamily.bundle" />

      <!-- Attributes -->
      <h3>{{ $t('headlines.attributes') }}</h3>

      <attribute-table :data="productFamily" :fields="['type', 'name', 'familyNumber', 'insertedAt', 'updatedAt']"
        v-if="productFamily.bundle">
        <template slot="type">{{ $t('types.bundle') }}</template>
        <template scope="insertedAt" slot="insertedAt">
          <timeago :auto-update="60" :since="insertedAt.value" />
        </template>
        <template scope="updatedAt" slot="updatedAt">
          <timeago :auto-update="60" :since="updatedAt.value" />
        </template>
      </attribute-table>

      <attribute-table :data="productFamily" :fields="['type', 'brand', 'model', 'familyNumber', 'selectors',
      'insertedAt', 'updatedAt']" v-else>
        <template slot="type">{{ $t('types.standard') }}</template>
        <template scope="brand" slot="brand">
          <router-link :to="{ name: 'edit-brand', params: { id: brand.value.id } }">{{ brand.value.name }}</router-link>
        </template>
        <template slot="selectors">
          <separated-list :items="selectors" />
        </template>
        <template scope="insertedAt" slot="insertedAt">
          <timeago :auto-update="60" :since="insertedAt.value" />
        </template>
        <template scope="updatedAt" slot="updatedAt">
          <timeago :auto-update="60" :since="updatedAt.value" />
        </template>
      </attribute-table>

      <!-- Categories -->
      <template v-if="productFamily.categories.length">
        <h3>{{ $t('headlines.productCategories', { count: productFamily.categories.length }) }}</h3>

        <product-category-assignment-table :product-categories="productFamily.categories" :show-actions="false" />
      </template>

      <!-- Variants -->
      <h3>{{ $t('headlines.productVariants', { count: variantsCount }) }}</h3>

      <link-button :to="{ name: newProductRouteName, query: { familyId: id } }" icon="plus" type="primary">{{
      $t('actions.add') }}</link-button>

      <common-button :disabled="productFamily.bundlings.length <= 1" @click="$refs.bundleWizard.show()"
        v-if="productFamily.bundle">{{ $t('actions.bundleWizard') }}</common-button>

      <bundle-wizard :product-family="productFamily" @update="$emit('reload-product-family')" ref="bundleWizard"
        v-if="productFamily.bundle" />

      <product-table :products="productFamily.products" use-variant-names v-if="hasVariants" />

      <div class="uk-placeholder uk-text-center" v-else>
        <icon class="uk-margin-small-right" name="warning" />
        {{ $t('hints.noVariants') }}
      </div>
    </div>
  </div>
</template>

<script>
import camelCase from 'lodash/camelCase';
import UIkit from 'uikit';
import AttributeTable from '@/components/AttributeTable';
import BundleWizard from '@/components/BundleWizard';
import CommonButton from '@/components/CommonButton';
import DataGridActions from '@/components/DataGridActions';
import deleteProductMutation
  from '@/api/mutations/delete-product-family.graphql';
import Dimensions from '@/components/Dimensions';
import EnergyLabel from '@/components/EnergyLabel';
import List from '@/components/List';
import Loader from '@/components/Loader';
import Modal from '@/components/Modal';
import ProductCategoryAssignmentTable
  from '@/components/ProductCategoryAssignmentTable';
import ProductFamilyBundlingManager
  from '@/components/ProductFamilyBundlingManager';
import ProductTable from '@/components/ProductTable';
import SeparatedList from '@/components/SeparatedList';
import Thumbnail from '@/components/Thumbnail';
import Toast from '@/helpers/Toast';
import View from '@/mixins/View';
import Weight from '@/components/Weight';
import Icon from '@/components/Icon';

export default {
  name: 'product-families-show-view',
  mixins: [View],
  components: {
    AttributeTable,
    BundleWizard,
    CommonButton,
    DataGridActions,
    Dimensions,
    EnergyLabel,
    List,
    Loader,
    Modal,
    ProductCategoryAssignmentTable,
    ProductFamilyBundlingManager,
    ProductTable,
    SeparatedList,
    Thumbnail,
    Weight,
    Icon,

  },
  props: {
    productFamily: Object,
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    selectors() {
      return (this.productFamily.selectors || []).map(
        selector => this.$t(`fields.${camelCase(selector)}`),
      );
    },
    isSuccessfullyLoaded() {
      return this.isLoaded && this.productFamily;
    },
    newProductRouteName() {
      if (this.productFamily.bundle) return 'new-bundle-product';
      return 'new-standard-product';
    },
    variantsCount() {
      const variants = this.productFamily.products || [];
      return variants.length;
    },
    hasVariants() {
      const products = this.productFamily.products;
      return products && products.length;
    },
    categoryNames() {
      return this.productFamily.categories.map(category => category.name);
    },
    hasActiveRevision() {
      return this.productFamily.activeRevision ?
        this.productFamily.activeRevision.active : false;
    },
  },
  methods: {
    deleteProductFamilyWhenConfirmed() {
      const message = this.$t('confirmations.delete',
        { name: this.productFamily.name });
      UIkit.modal
        .confirm(message)
        .then(() => { this.deleteProductFamily(); })
        .catch(() => { });
    },
    async deleteProductFamily() {
      await this.$apollo.mutate({
        mutation: deleteProductMutation,
        variables: { id: this.productFamily.id },
      });
      const message = this.$t('toasts.deleted',
        { name: this.productFamily.name });
      Toast.success(message);
      this.$router.push({ name: 'product-families' });
    },
  },
  i18n: {
    messages: {
      de: {
        actions: {
          bundleWizard: 'Bundle-Assistent',
        },
        confirmations: {
          delete: `Produktfamilie <strong>{name}</strong> wirklich löschen?
                   Alle zugehörigen Produkte, Bilder und Angebote werden
                   ebenfalls gelöscht.`,
        },
        headlines: {
          attributes: 'Eigenschaften',
          bundledFamilies: 'Bundle-Bestandteile',
          productCategories: 'Kategorien ({count})',
          productVariants: 'Produktvarianten ({count})',
        },
        hints: {
          noVariants:
            'Diese Produktfamilie hat aktuell keine Produktvarianten.',
          activeRevision: 'Hier ist eine aktive Überarbeitung.',
        },
        toasts: {
          deleted: 'Produktfamilie gelöscht: {name}',
        },
        types: {
          standard: 'Standard',
          bundle: 'Bundle',
        },
      },
    },
  },
};
</script>

<style lang="scss">
.action-buttons-container {
  display: flex;
  align-items: center;
  /* Align items vertically */
}

.action-buttons {
  display: flex;
  gap: 10px;
  /* Adjust spacing between buttons */
}

.active-revision-indicator {
  margin-left: auto;
  /* Push the "ACTIVE" indicator to the right */
  padding-right: 10px;
}
</style>



// WEBPACK FOOTER //
// ShowView.vue?3c4f1e18